import {
	Box,
	Breadcrumbs,
	Button,
	Typography,
	styled,
	useMediaQuery,
	useTheme,
} from '@mui/material'
import { selectPagePosition } from 'hooks/pagePos'
import { useSelector } from 'react-redux'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { memo } from 'react'
import SaveAsTwoToneIcon from '@mui/icons-material/SaveAsTwoTone'
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone'
import AddToPhotosTwoToneIcon from '@mui/icons-material/AddToPhotosTwoTone'
import ContentPasteSearchTwoToneIcon from '@mui/icons-material/ContentPasteSearchTwoTone'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import DownloadIcon from '@mui/icons-material/Download'

type PagePositionProps = {
	actionAdd: () => void
	actionSave: () => void
	actionDel: () => void
	actionSearch: () => void
	actionReset: () => void
	actionExcelDownload: () => void
	disabledAdd: boolean
	disabledSave: boolean
	disabledDel: boolean
	disabledSearch: boolean
	disabledReset: boolean
	disabledExcelDownload: boolean
	hideAdd?: boolean
	hideDel?: boolean
	hideSave?: boolean
	hideSearch?: boolean
	hideReset?: boolean
	hideExcelDownload?: boolean
	otherButtons?: React.ReactNode
}

const Container = styled('div')(({ theme }) => ({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	padding: '1rem',
	borderTopLeftRadius: '0.5rem',
	borderTopRightRadius: '0.5rem',
	backgroundColor:
		theme.palette.mode === 'dark' ? 'rgba(50,48,54, 0.65)' : '#fff',
	borderBottom: `1px solid ${theme.palette.divider}`,
}))

const PagePosition: React.FC<PagePositionProps> = ({
	actionAdd,
	actionSave,
	actionDel,
	actionSearch,
	actionReset,
	actionExcelDownload,
	disabledAdd,
	disabledSave,
	disabledDel,
	disabledSearch,
	disabledReset,
	disabledExcelDownload,
	hideAdd,
	hideDel,
	hideSave,
	hideSearch,
	hideReset,
	hideExcelDownload,
	otherButtons,
}) => {
	const { home, pos1, pos2 } = useSelector(selectPagePosition)
	const theme = useTheme()
	const isMdUp = useMediaQuery(theme.breakpoints.up('md'))

	return (
		<Container>
			<Breadcrumbs
				separator={<NavigateNextIcon fontSize="small" />}
				aria-label="breadcrumb"
			>
				{pos1?.title && <Typography>{pos1.title}</Typography>}
				{pos2?.title && (
					<Typography>
						<b>{pos2.title}</b>
					</Typography>
				)}
			</Breadcrumbs>
			<Box>
				<Button
					color="success"
					variant="contained"
					startIcon={isMdUp && <RotateLeftIcon />}
					sx={{
						marginRight: '0.5rem',
						padding: isMdUp ? '4px 10px' : '4px 6px',
						minWidth: isMdUp ? '64px' : '50px',
						display: hideReset ? 'none' : 'inline-flex',
					}}
					disabled={disabledReset}
					onClick={actionReset}
				>
					초기화
				</Button>
				<Button
					color="primary"
					variant="contained"
					startIcon={isMdUp && <ContentPasteSearchTwoToneIcon />}
					sx={{
						marginRight: '0.5rem',
						padding: isMdUp ? '4px 10px' : '4px 6px',
						minWidth: isMdUp ? '64px' : '50px',
						display: hideSearch ? 'none' : 'inline-flex',
					}}
					disabled={disabledSearch}
					onClick={actionSearch}
				>
					검색
				</Button>
				<Button
					color="secondary"
					variant="contained"
					startIcon={isMdUp && <AddToPhotosTwoToneIcon />}
					sx={{
						marginRight: '0.5rem',
						padding: isMdUp ? '4px 10px' : '4px 6px',
						minWidth: isMdUp ? '64px' : '50px',
						display: hideAdd ? 'none' : 'inline-flex',
					}}
					onClick={actionAdd}
					disabled={disabledAdd}
				>
					추가
				</Button>
				<Button
					color="primary"
					variant="contained"
					startIcon={isMdUp && <SaveAsTwoToneIcon />}
					sx={{
						marginRight: '0.5rem',
						padding: isMdUp ? '4px 10px' : '4px 6px',
						minWidth: isMdUp ? '64px' : '50px',
						display: hideSave ? 'none' : 'inline-flex',
					}}
					onClick={actionSave}
					disabled={disabledSave}
				>
					저장
				</Button>
				<Button
					color="warning"
					variant="contained"
					startIcon={isMdUp && <DeleteForeverTwoToneIcon />}
					sx={{
						marginRight: '0.5rem',
						padding: isMdUp ? '4px 10px' : '4px 6px',
						minWidth: isMdUp ? '64px' : '50px',
						display: hideDel ? 'none' : 'inline-flex',
					}}
					onClick={actionDel}
					disabled={disabledDel}
				>
					삭제
				</Button>
				<Button
					color="info"
					variant="contained"
					startIcon={isMdUp && <DownloadIcon />}
					sx={{
						padding: isMdUp ? '4px 10px' : '4px 6px',
						minWidth: isMdUp ? '64px' : '50px',
						display: hideExcelDownload ? 'none' : 'inline-flex',
					}}
					onClick={actionExcelDownload}
					disabled={disabledExcelDownload}
				>
					엑셀 다운로드
				</Button>
				{otherButtons}
			</Box>
		</Container>
	)
}

export default memo(PagePosition)
